import { useCallback, useEffect, useMemo, useState } from "react"
import { ChevronLeft, ChevronRight, Home, Images, Loader2, X } from "lucide-react"
import { Link, useLocation, useParams } from "react-router"

import * as settingsService from "@/services/settingsService"
import type { GalleryAlbum } from "@/services/settingsService"
import { resolveGalleryImageUrl, STATIC_GALLERY_ALBUMS } from "./gallery-fallback"

type GalleryLocationState = {
  album?: GalleryAlbum
}

export const GalleryAlbumPage = () => {
  const { albumId } = useParams()
  const location = useLocation()
  const routeAlbum = (location.state as GalleryLocationState | null)?.album
  const [albums, setAlbums] = useState<GalleryAlbum[]>(
    routeAlbum ? [routeAlbum] : [],
  )
  const [loading, setLoading] = useState(!routeAlbum)
  const [activeImageIndex, setActiveImageIndex] = useState<number | null>(null)

  useEffect(() => {
    let cancelled = false

    async function fetchAlbums() {
      try {
        const data = await settingsService.getGallery()
        if (!cancelled) setAlbums(data.length > 0 ? data : STATIC_GALLERY_ALBUMS)
      } catch (error) {
        console.warn("Không tải được album ảnh, đang dùng dữ liệu dự phòng", error)
        if (!cancelled) setAlbums(STATIC_GALLERY_ALBUMS)
      } finally {
        if (!cancelled) setLoading(false)
      }
    }

    void fetchAlbums()
    return () => {
      cancelled = true
    }
  }, [])

  const album = useMemo(
    () => albums.find((item) => item.id === albumId) ?? routeAlbum,
    [albumId, albums, routeAlbum],
  )
  const relatedAlbums = useMemo(
    () => albums.filter((item) => item.id !== albumId).slice(0, 5),
    [albumId, albums],
  )

  const showPreviousImage = useCallback(() => {
    if (!album || activeImageIndex === null || album.images.length === 0) return
    setActiveImageIndex(
      activeImageIndex === 0 ? album.images.length - 1 : activeImageIndex - 1,
    )
  }, [activeImageIndex, album])

  const showNextImage = useCallback(() => {
    if (!album || activeImageIndex === null || album.images.length === 0) return
    setActiveImageIndex(
      activeImageIndex === album.images.length - 1 ? 0 : activeImageIndex + 1,
    )
  }, [activeImageIndex, album])

  useEffect(() => {
    if (activeImageIndex === null) return
    const previousOverflow = document.body.style.overflow
    document.body.style.overflow = "hidden"

    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === "Escape") setActiveImageIndex(null)
      if (event.key === "ArrowLeft") showPreviousImage()
      if (event.key === "ArrowRight") showNextImage()
    }

    window.addEventListener("keydown", handleKeyDown)
    return () => {
      document.body.style.overflow = previousOverflow
      window.removeEventListener("keydown", handleKeyDown)
    }
  }, [activeImageIndex, showNextImage, showPreviousImage])

  if (loading && !album) {
    return (
      <div className="flex min-h-[70vh] items-center justify-center bg-[#1b1307] text-[#c8bba8]">
        <Loader2 className="size-6 animate-spin text-[#c1a35f]" aria-hidden="true" />
        <span className="ml-3 text-sm">Đang tải album...</span>
      </div>
    )
  }

  if (!album) {
    return (
      <div className="flex min-h-[70vh] flex-col items-center justify-center bg-[#1b1307] px-5 text-center text-[#f3ecdf]">
        <Images className="size-12 text-[#c1a35f]" aria-hidden="true" />
        <h1 className="mt-5 font-serif text-3xl font-semibold">Không tìm thấy album</h1>
        <p className="mt-2 text-sm text-[#b8aa96]">Album có thể đã được di chuyển hoặc không còn tồn tại.</p>
        <Link
          to="/thu-vien-anh"
          className="mt-7 border border-[#c1a35f]/60 px-5 py-2.5 text-xs font-semibold uppercase tracking-wider text-[#d7bd7a] transition hover:bg-[#c1a35f] hover:text-[#171108]"
        >
          Về thư viện ảnh
        </Link>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-[#1b1307] text-[#f3ecdf]">
      <header className="mx-auto max-w-[1180px] px-5 pb-16 pt-24 text-center sm:px-8 sm:pb-20 sm:pt-28">
        <nav
          aria-label="Breadcrumb"
          className="flex flex-wrap items-center justify-center gap-2 text-xs text-[#b8aa96] sm:text-sm"
        >
          <Link to="/" className="inline-flex items-center gap-1.5 transition-colors hover:text-[#d2b775]">
            <Home className="size-3.5" aria-hidden="true" />
            Trang chủ
          </Link>
          <span aria-hidden="true">›</span>
          <Link to="/thu-vien-anh" className="transition-colors hover:text-[#d2b775]">
            Thư viện ảnh
          </Link>
          <span aria-hidden="true">›</span>
          <span className="text-[#e1d6c6]">{album.title}</span>
        </nav>

        <p className="mt-8 text-[10px] font-semibold uppercase tracking-[0.38em] text-[#c1a35f] sm:mt-10">
          Nhà Cam Homestay
        </p>
        <h1 className="mt-3 font-serif text-4xl font-semibold tracking-tight text-[#f7f0e4] sm:text-5xl lg:text-6xl">
          {album.title}
        </h1>
        {album.description && (
          <p className="mx-auto mt-4 max-w-3xl text-sm leading-6 text-[#b8aa96] sm:text-base">
            {album.description}
          </p>
        )}
        <div className="mt-5 inline-flex items-center gap-2 text-xs text-[#b8aa96] sm:text-sm">
          <Images className="size-4" aria-hidden="true" />
          <span>{album.images.length} ảnh</span>
        </div>
      </header>

      <main className="mx-auto max-w-[1180px] px-5 pb-24 sm:px-8 sm:pb-28 lg:pb-32">
        {album.images.length === 0 ? (
          <div className="flex min-h-[320px] flex-col items-center justify-center text-center text-[#968b7c]">
            <Images className="size-11" aria-hidden="true" />
            <p className="mt-4 font-serif text-xl text-[#d8cdbd]">Album chưa có ảnh</p>
          </div>
        ) : (
          <div className="columns-1 gap-4 sm:columns-2 lg:columns-3 xl:columns-4">
            {album.images.map((image, index) => (
              <button
                key={image.id}
                type="button"
                onClick={() => setActiveImageIndex(index)}
                className="group relative mb-4 block w-full break-inside-avoid overflow-hidden rounded-xl bg-white/5 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#c1a35f]"
                aria-label={`Mở ảnh ${index + 1} trong album ${album.title}`}
              >
                <img
                  src={resolveGalleryImageUrl(image.url)}
                  alt={`${album.title} - ảnh ${index + 1}`}
                  className="h-auto w-full object-cover transition duration-500 group-hover:scale-[1.025] group-hover:brightness-90"
                  loading={index < 4 ? "eager" : "lazy"}
                />
                <span className="pointer-events-none absolute inset-x-3 bottom-3 translate-y-2 rounded-full bg-black/55 px-3 py-1.5 text-xs text-white/90 opacity-0 backdrop-blur-sm transition duration-300 group-hover:translate-y-0 group-hover:opacity-100">
                  {album.title} · Ảnh {index + 1}
                </span>
              </button>
            ))}
          </div>
        )}
      </main>

      {relatedAlbums.length > 0 && (
        <section className="bg-[#151819] px-5 py-16 sm:px-8 sm:py-20" aria-labelledby="related-albums-title">
          <div className="mx-auto max-w-[1180px]">
            <h2 id="related-albums-title" className="text-center font-serif text-2xl font-semibold text-[#f3ecdf] sm:text-3xl">
              Album khác
            </h2>
            <div className="mt-8 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5 lg:gap-4">
              {relatedAlbums.map((relatedAlbum) => (
                <Link
                  key={relatedAlbum.id}
                  to={`/thu-vien-anh/${encodeURIComponent(relatedAlbum.id)}`}
                  state={{ album: relatedAlbum }}
                  className="group relative aspect-[7/4] overflow-hidden rounded-lg bg-white/5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#c1a35f]"
                  aria-label={`Xem album ${relatedAlbum.title}`}
                >
                  <img
                    src={resolveGalleryImageUrl(relatedAlbum.cover)}
                    alt=""
                    className="size-full object-cover transition duration-500 group-hover:scale-105"
                    loading="lazy"
                  />
                  <span className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/20 to-transparent" />
                  <span className="absolute inset-x-3 bottom-3 line-clamp-2 text-xs font-semibold text-white sm:text-sm">
                    {relatedAlbum.title}
                  </span>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {activeImageIndex !== null && album.images[activeImageIndex] && (
        <div
          role="dialog"
          aria-modal="true"
          aria-label="Xem ảnh toàn màn hình"
          onClick={() => setActiveImageIndex(null)}
          className="fixed inset-0 z-[80] flex items-center justify-center bg-black/95 px-4 py-16"
        >
          <button
            type="button"
            onClick={() => setActiveImageIndex(null)}
            aria-label="Đóng ảnh"
            className="absolute right-4 top-4 rounded-full bg-white/10 p-3 text-white transition hover:bg-white/20 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#c1a35f] sm:right-6 sm:top-6"
          >
            <X className="size-5" aria-hidden="true" />
          </button>

          {album.images.length > 1 && (
            <button
              type="button"
              onClick={(event) => {
                event.stopPropagation()
                showPreviousImage()
              }}
              aria-label="Ảnh trước"
              className="absolute left-3 rounded-full bg-white/10 p-3 text-white transition hover:bg-white/20 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#c1a35f] sm:left-6"
            >
              <ChevronLeft className="size-6" aria-hidden="true" />
            </button>
          )}

          <img
            src={resolveGalleryImageUrl(album.images[activeImageIndex].url)}
            alt={`${album.title} - ảnh ${activeImageIndex + 1}`}
            onClick={(event) => event.stopPropagation()}
            className="max-h-full max-w-full rounded-lg object-contain shadow-2xl"
          />

          {album.images.length > 1 && (
            <button
              type="button"
              onClick={(event) => {
                event.stopPropagation()
                showNextImage()
              }}
              aria-label="Ảnh tiếp theo"
              className="absolute right-3 rounded-full bg-white/10 p-3 text-white transition hover:bg-white/20 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#c1a35f] sm:right-6"
            >
              <ChevronRight className="size-6" aria-hidden="true" />
            </button>
          )}

          <p className="absolute bottom-5 text-xs font-medium text-white/65">
            Ảnh {activeImageIndex + 1} / {album.images.length}
          </p>
        </div>
      )}
    </div>
  )
}
